import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/common/Modal'
import { Button } from '@/components/common/Button'
import { CheckCircle2, FileText, MinusCircle, Eye, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react'
import { DAY_ORDER } from './typesV2'
import { parseTimeToMinutes, normalizeTimeSlot } from './timeUtils'

interface BulkImportResultProps {
  batchId?: string
  sections: Array<{
    section_index: number
    section_identifier: string | null
    section_pages: number[]
    session_id: string | null
    status: string
    error: string | null
    complete_entries: number
    total_entries: number
  }>
  previewEntries?: Record<string, Array<{
    teacher_code?: string
    day_of_week?: string
    time_slot_label?: string
    lesson_number?: number
    subject?: string
    room?: string
    class_name?: string
  }>>
  onRetry: (sessionId: string) => Promise<void>
  onResolve: (sessionId: string) => Promise<void>
  onImportAll: () => Promise<void>
  onPreviewEntries: (sessionId: string) => Promise<void>
  onAssignTeachers?: () => Promise<void>
  onClose: () => void
}

interface ErrorSummary {
  type: 'failed' | 'incomplete' | 'pending_resolution' | 'pending_assignment'
  count: number
  sections: Array<{ index: number; identifier: string | null; error: string | null; errorNumber?: number }>
}

function ErrorSummaryPanel({ summaries, t }: { summaries: ErrorSummary[]; t: any }) {
  const totalErrors = summaries.reduce((sum, s) => sum + s.count, 0)

  if (totalErrors === 0) return null

  return (
    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <h3 className="font-semibold text-red-800 mb-2">
        {totalErrors} {totalErrors === 1 ? 'Fehler' : 'Fehler'} gefunden
      </h3>
      <div className="space-y-2">
        {summaries.map((summary) => (
          <div key={summary.type} className="text-sm">
            <span className="font-medium text-red-700">
              {summary.type === 'failed' && (t('bulkImport_failedPages') as string || 'Fehlgeschlagene Seiten')}
              {summary.type === 'incomplete' && (t('bulkImport_incompletePages') as string || 'Unvollständige Seiten')}
              {summary.type === 'pending_resolution' && (t('bulkImport_resolutionRequired') as string || 'Auflösung erforderlich')}
              {summary.type === 'pending_assignment' && (t('bulkImport_assignmentRequired') as string || 'Lehrer-Zuordnung erforderlich')}
            </span>
            <span className="text-red-600 ml-2">({summary.count})</span>
            {summary.sections.map((section) => (
              <div key={section.index} className="text-red-500 ml-4 text-xs">
                • {section.errorNumber ? `Fehler ${section.errorNumber}: ` : ''}{(t('bulkImport_pageError') as string || 'Seite {{page}}: {{error}}')
                    .replace('{{page}}', String(section.index + 1))
                    .replace('{{error}}', section.error || section.identifier || 'Unbekannt')}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

interface ExtractedEntry {
  teacher_code?: string
  day_of_week?: string
  time_slot_label?: string
  lesson_number?: number
  subject?: string
  room?: string
  class_name?: string
}

function SectionEntryPreview({ entries }: { entries: ExtractedEntry[] }) {
  const [expanded, setExpanded] = useState(false)
  const { t } = useTranslation()

  const sortedEntries = useMemo(() => {
    return entries
      .map((entry, originalIndex) => ({ entry, originalIndex }))
      .sort((a, b) => {
        const dayA = a.entry.day_of_week ?? ''
        const dayB = b.entry.day_of_week ?? ''
        const dayDiff = DAY_ORDER.indexOf(dayA) - DAY_ORDER.indexOf(dayB)
        if (dayDiff !== 0) return dayDiff

        return parseTimeToMinutes(normalizeTimeSlot(a.entry.time_slot_label)) - parseTimeToMinutes(normalizeTimeSlot(b.entry.time_slot_label))
      })
  }, [entries])

  if (!entries.length) return null

  const dayLabels: Record<string, string> = {
    MON: 'Mo', TUE: 'Di', WED: 'Mi', THU: 'Do', FRI: 'Fr',
  }

  return (
    <div className="mt-2 border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 text-sm font-medium"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        {(t('bulkImport_previewEntries') as string) || 'Einträge anzeigen'} ({entries.length})
      </button>
      {expanded && (
        <div className="max-h-64 overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-2 py-1 text-left font-medium text-gray-600">#</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Tag</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Zeit</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Lehrer</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Fach</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Klasse</th>
                <th className="px-2 py-1 text-left font-medium text-gray-600">Raum</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries.map(({ entry, originalIndex }) => (
                <tr key={originalIndex} className="border-t hover:bg-gray-50">
                  <td className="px-2 py-1">{entry.lesson_number || '-'}</td>
                  <td className="px-2 py-1">{dayLabels[entry.day_of_week || ''] || entry.day_of_week || '-'}</td>
                  <td className="px-2 py-1">{entry.time_slot_label || '-'}</td>
                  <td className="px-2 py-1">{entry.teacher_code || '-'}</td>
                  <td className="px-2 py-1">{entry.subject || '-'}</td>
                  <td className="px-2 py-1">{entry.class_name || '-'}</td>
                  <td className="px-2 py-1">{entry.room || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function BulkImportResult({ sections, previewEntries = {}, onRetry, onImportAll, onPreviewEntries, onAssignTeachers, onClose }: BulkImportResultProps) {
  const { t } = useTranslation()
  const [previewLoading, setPreviewLoading] = useState<Record<string, boolean>>({})
  const [visiblePreviews, setVisiblePreviews] = useState<Record<string, boolean>>({})

  const handlePreview = useCallback(async (sessionId: string) => {
    if (visiblePreviews[sessionId]) {
      // Toggle off
      setVisiblePreviews(prev => ({ ...prev, [sessionId]: false }))
      return
    }

    setPreviewLoading(prev => ({ ...prev, [sessionId]: true }))
    try {
      await onPreviewEntries(sessionId)
      setVisiblePreviews(prev => ({ ...prev, [sessionId]: true }))
    } catch {
      // ignore
    } finally {
      setPreviewLoading(prev => ({ ...prev, [sessionId]: false }))
    }
  }, [visiblePreviews, onPreviewEntries])

  const errorSummaries: ErrorSummary[] = useMemo(() => {
    const failed = sections.filter(s => s.status === 'failed')
    const incomplete = sections.filter(s => s.status === 'resolved' && s.complete_entries < s.total_entries)
    const pending = sections.filter(s => s.status === 'pending_resolution')
    const pendingAssignment = sections.filter(s => s.status === 'pending_assignment')

    const result: ErrorSummary[] = []
    if (failed.length > 0) {
      result.push({
        type: 'failed',
        count: failed.length,
        sections: failed.map((s, idx) => ({ index: s.section_index, identifier: s.section_identifier, error: s.error, errorNumber: idx + 1 }))
      })
    }
    if (incomplete.length > 0) {
      const startNum = failed.length + 1
      result.push({
        type: 'incomplete',
        count: incomplete.length,
        sections: incomplete.map((s, idx) => ({ index: s.section_index, identifier: s.section_identifier, error: null, errorNumber: startNum + idx }))
      })
    }
    if (pending.length > 0) {
      const startNum = failed.length + incomplete.length + 1
      result.push({
        type: 'pending_resolution',
        count: pending.length,
        sections: pending.map((s, idx) => ({ index: s.section_index, identifier: s.section_identifier, error: null, errorNumber: startNum + idx }))
      })
    }
    if (pendingAssignment.length > 0) {
      const startNum = failed.length + incomplete.length + pending.length + 1
      result.push({
        type: 'pending_assignment',
        count: pendingAssignment.length,
        sections: pendingAssignment.map((s, idx) => ({ index: s.section_index, identifier: s.section_identifier, error: null, errorNumber: startNum + idx }))
      })
    }
    return result
  }, [sections])

  const errorSectionsMap = useMemo(() => {
    const map: Record<number, number> = {}
    let errorNum = 1
    for (const s of sections) {
      if (s.status === 'failed' || 
          (s.status === 'resolved' && s.complete_entries < s.total_entries) || 
          s.status === 'pending_resolution' || 
          s.status === 'pending_assignment') {
        map[s.section_index] = errorNum++
      }
    }
    return map
  }, [sections])

  const resolvedCount = sections.filter((s) => s.status === 'resolved').length
  const importedCount = sections.filter((s) => s.status === 'imported').length
  const failedCount = sections.filter((s) => s.status === 'failed').length
  const pendingResolutionCount = sections.filter((s) => s.status === 'pending_resolution').length
  const pendingAssignmentCount = sections.filter((s) => s.status === 'pending_assignment').length

  const statusIcons: Record<string, React.ReactNode> = {
    resolved: <CheckCircle2 className="h-4 w-4 text-green-500" />,
    imported: <CheckCircle2 className="h-4 w-4 text-blue-500" />,
    failed: <MinusCircle className="h-4 w-4 text-red-500" />,
    skipped: <FileText className="h-4 w-4 text-gray-400" />,
    pending_resolution: <MinusCircle className="h-4 w-4 text-yellow-500" />,
    incomplete: <AlertTriangle className="h-4 w-4 text-orange-500" />,
    pending_assignment: <MinusCircle className="h-4 w-4 text-orange-500" />,
  }

  return (
    <Modal isOpen={true} onClose={onClose} title={t('bulkImport_result_title') as string} size="lg">
      <div className="space-y-4">
        {/* Error Summary Panel */}
        <ErrorSummaryPanel summaries={errorSummaries} t={t} />

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="text-2xl font-bold text-green-700">{resolvedCount + importedCount}</div>
            <div className="text-sm text-green-600">{(t('bulkImport_successful') as string) || 'Erfolgreich'}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-2xl font-bold text-red-700">{failedCount + pendingResolutionCount + pendingAssignmentCount}</div>
            <div className="text-sm text-red-600">{(t('bulkImport_withErrors') as string) || 'Mit Fehlern'}</div>
          </div>
        </div>



        {/* Sections List */}
        {sections.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {sections.map((section, idx) => {
              const errorNum = errorSectionsMap[section.section_index]
              return (
              <div
                // Use combination of section_index, session_id, and array index to ensure uniqueness
                key={`${section.section_index}-${section.session_id || idx}`}
                className="border rounded-lg overflow-hidden"
              >
                <div className="flex items-center gap-3 p-2 hover:bg-gray-50">
                  <div className="flex items-center gap-1">
                    {statusIcons[section.status] || <FileText className="h-4 w-4 text-gray-400" />}
                    {errorNum && <span className="text-xs font-medium text-red-600">#{errorNum}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                      {section.section_identifier || `Seite ${section.section_pages?.join(', ')}`}
                    </div>
                    <div className="text-xs text-gray-500">
                      {section.complete_entries > 0 && section.total_entries > 0
                        ? `${section.complete_entries}/${section.total_entries} Einträge`
                        : section.section_pages?.length > 0 && `Seite ${section.section_pages.join(', ')}`}
                      {section.error && <span className="text-red-500 ml-2">{section.error}</span>}
                    </div>
                  </div>
                  {/* Preview button for resolved/imported/pending_assignment sections */}
                  {(section.status === 'resolved' || section.status === 'imported' || section.status === 'pending_assignment') && section.session_id && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                      onClick={() => handlePreview(section.session_id!)}
                      disabled={previewLoading[section.session_id || '']}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      {(t('bulkImport_preview') as string) || 'Vorschau'}
                    </Button>
                  )}
                  {/* Action buttons per section */}
                  {section.status === 'failed' && section.session_id && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => onRetry(section.session_id!)}
                    >
                      {(t('files_fallback_retry') as string) || 'Retry'}
                    </Button>
                  )}
                  {section.status === 'pending_assignment' && onAssignTeachers && (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      onClick={onAssignTeachers}
                    >
                      {(t('bulkImport_assignTeachers') as string) || 'Lehrer zuordnen'}
                    </Button>
                  )}
                </div>
                {/* Entry Preview */}
                {section.session_id && visiblePreviews[section.session_id] && previewEntries[section.session_id] && (
                  <div className="px-2 pb-2">
                    <SectionEntryPreview
                      entries={previewEntries[section.session_id] || []}
                    />
                  </div>
                )}
              </div>
              )
            })}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2 border-t">
          {(resolvedCount > 0 || importedCount > 0) && (
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={onImportAll}
            >
              {(t('bulkImport_importAll') as string) || 'Alle importieren'}
            </Button>
          )}
          <Button variant="secondary" size="sm" className="flex-1" onClick={onClose}>
            {(t('bulkImport_result_close') as string) || 'Schliessen'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
